import { z } from 'zod';
import type { BlockStimulus, StimulusType, StimulusTheme } from '../types/payloads/stimulus.types';

export const StimulusTypeSchema = z.enum([
  'poem',
  'news',
  'historical_source',
  'case_study',
  'scientific_data',
  'prose',
  'general',
]);

export const StimulusThemeSchema = z.enum(['newspaper', 'letter', 'poem', 'document', 'prose']);

export const ContextImageFieldsSchema = {
  imagePrompt: z.string().trim().min(3).optional(),
  wikiQuery: z.string().trim().min(2).optional(),
  imageUrl: z.string().url().optional(),
} as const;

export const BlockStimulusSchema = z.object({
  title: z.string().trim().min(1).optional(),
  type: StimulusTypeSchema,
  content: z.string().trim().min(12, 'stimulus content too short'),
  theme: StimulusThemeSchema.optional(),
  ...ContextImageFieldsSchema,
});

/** Campo stimulus opcional — todos los payloads de bloque evaluativo lo heredan */
export const OptionalBlockStimulusShape = {
  stimulus: BlockStimulusSchema.optional(),
  ...ContextImageFieldsSchema,
} as const;

/** Extiende un esquema Zod de payload con stimulus opcional */
export function withOptionalBlockStimulus<T extends z.ZodRawShape>(shape: T) {
  return z.object({ ...shape, ...OptionalBlockStimulusShape });
}

export function parseBlockStimulus(raw: unknown): BlockStimulus | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const result = BlockStimulusSchema.safeParse(raw);
  return result.success ? result.data : undefined;
}

/** Extrae stimulus validado de un payload crudo (agnóstico al mode) */
export function parseOptionalBlockStimulusFromPayload(raw: unknown): BlockStimulus | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  return parseBlockStimulus((raw as { stimulus?: unknown }).stimulus);
}

export function inferStimulusType(content: string, hint = ''): StimulusType {
  const blob = `${hint} ${content}`.toLowerCase();
  if (/verso|estrofa|rima|poema|metáfora|símil|recurso poético|lírica/.test(blob)) return 'poem';
  if (/noticia|titular|prensa|comunicado|reportaje/.test(blob)) return 'news';
  if (/carta|documento|fuente|decreto|tratado|testimonio|archivo|siglo/i.test(blob)) return 'historical_source';
  if (/experimento|laboratorio|hipótesis|variable|datos|muestra|ml\b|°c|ph\b/.test(blob)) return 'scientific_data';
  if (/caso|empresa|escenario|situación problem/.test(blob)) return 'case_study';
  if (/\n/.test(content) && content.split('\n').filter(Boolean).length >= 2) return 'poem';
  return 'prose';
}
